// MTXbudget: turns Actual's schedules into the dated occurrences the
// calculation core works on, reusing Actual's own recurrence logic.
import type { ScheduleEntity } from '#types/models';

import type {
  Debt,
  Occurrence,
  PayConfig,
  PayInput,
  Project,
  Reserve,
  Saving,
} from './mtxbudget';
import { fortnightStart } from './mtxbudget';
import type { Arrangement, Provided } from './mtxbudget-completer';
import type { ExtraIncome } from './mtxbudget-parcours';
import {
  extractScheduleConds,
  getNextDateAfter,
  getScheduledAmount,
  scheduleIsRecurring,
} from './schedules';

/**
 * A debt tied to an account takes its balance from that account; the one-off
 * schedules tied to it are the repayments already planned for it.
 */
export type ConfiguredDebt = Debt & {
  accountId?: string;
  repaymentScheduleIds?: string[];
  /** Schedules whose payments reduce this debt (its minimum, its instalment). */
  paymentScheduleIds?: string[];
  /** Credit limit of a card or line, in cents. */
  creditLimit?: number;
};

export type MtxConfig = {
  pay: PayConfig;
  /** Accounts whose balance counts as cash on hand. */
  cashAccountIds: string[];
  debts: ConfiguredDebt[];
  reserves: Reserve[];
  projects: Project[];
  /** Registered savings plans, fed once the debts are cleared. */
  savings: Saving[];
  /** Cents to set aside before accelerating any debt; 0 for none. */
  emergencyFundTarget: number;
  /** What is known about each pay, keyed by payday. */
  pays: Record<string, PayInput>;
  /** Optimise proposals the user accepted. */
  decisions: { date: string; shortfall: number; optionIds: string[] }[];
  /** Payments the user ticked as made, keyed by occurrence id. */
  paid: Record<string, string>;
  /** Cents kept in the account after every pay. */
  cushion: number;
  /** Money other than the salary, usable from its date. */
  extraIncomes: ExtraIncome[];
  /** Payments switched off by hand, keyed by occurrence id. */
  skipped: Record<string, true>;
  /** The most hours a week the user is ready to work. */
  maxHoursPerWeek: number;
  /** Amounts the banks never send, as the user gave them, by occurrence id. */
  provided: Record<string, Provided>;
  /** Balances the user gave for accounts not read this week, by account id. */
  providedBalances: Record<string, Provided>;
  /** Items put off with « Plus tard », by id: the day they come back. */
  later: Record<string, string>;
  /** Schedules whose creditor lets a bill be paid in part. */
  partialOk: string[];
  /** Bills paid in part, as arranged with the creditor, by occurrence id. */
  arrangements: Record<string, Arrangement>;
};

/** The most hours a week assumed unless told otherwise. */
export const DEFAULT_MAX_HOURS = 45;

/** What the account keeps after every pay unless told otherwise: 100 $. */
export const DEFAULT_CUSHION = 10000;

export const MTX_CONFIG_PREF = 'mtxbudget.config';

export function parseMtxConfig(raw: string | undefined): MtxConfig | null {
  if (!raw) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as Partial<MtxConfig>;
    if (!value.pay || !Array.isArray(value.pay.taxPoints)) {
      return null;
    }
    return {
      pay: value.pay,
      cashAccountIds: value.cashAccountIds ?? [],
      debts: value.debts ?? [],
      reserves: value.reserves ?? [],
      projects: value.projects ?? [],
      savings: value.savings ?? [],
      emergencyFundTarget: value.emergencyFundTarget ?? 0,
      pays: value.pays ?? {},
      decisions: value.decisions ?? [],
      paid: value.paid ?? {},
      cushion: value.cushion ?? DEFAULT_CUSHION,
      extraIncomes: value.extraIncomes ?? [],
      skipped: value.skipped ?? {},
      maxHoursPerWeek: value.maxHoursPerWeek ?? DEFAULT_MAX_HOURS,
      provided: value.provided ?? {},
      providedBalances: value.providedBalances ?? {},
      later: value.later ?? {},
      partialOk: value.partialOk ?? [],
      arrangements: value.arrangements ?? {},
    };
  } catch {
    return null;
  }
}

/**
 * Every occurrence of every active schedule between `from` and `to`
 * (inclusive). `paidIds` lists schedules whose next occurrence is already
 * matched by a real transaction.
 */
export function expandSchedules(
  schedules: readonly ScheduleEntity[],
  from: string,
  to: string,
  paidIds: ReadonlySet<string> = new Set(),
): Occurrence[] {
  const occurrences: Occurrence[] = [];
  for (const schedule of schedules) {
    if (schedule.completed || schedule.tombstone || !schedule.next_date) {
      continue;
    }
    const { date: dateCond } = extractScheduleConds(schedule._conditions);
    const amount = getScheduledAmount(schedule._amount);
    // A rule that ended before its next date has nothing left to pay.
    const rule =
      dateCond && typeof dateCond.value === 'object' ? dateCond.value : null;
    const ended =
      rule?.endMode === 'on_date' &&
      rule.endDate !== undefined &&
      rule.endDate < schedule.next_date;
    const dates: string[] =
      paidIds.has(schedule.id) || ended ? [] : [schedule.next_date];
    if (dateCond && scheduleIsRecurring(dateCond)) {
      let last = schedule.next_date;
      // Bounded: a daily schedule over a year stays under this.
      for (let i = 0; i < 400 && last <= to; i++) {
        const next = getNextDateAfter(dateCond, last);
        if (next === null || next <= last) {
          break;
        }
        dates.push(next);
        last = next;
      }
    }
    const recurring = Boolean(dateCond && scheduleIsRecurring(dateCond));
    for (const date of dates) {
      if (date >= from && date <= to) {
        occurrences.push({
          id: `${schedule.id}/${date}`,
          name: schedule.name ?? '',
          date,
          amount,
          scheduleId: schedule.id,
          account: schedule._account,
          approximate: schedule._amountOp !== 'is',
          recurring,
        });
      }
    }
  }
  return occurrences.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * The day the plan starts from. The balances only hold what the banks have
 * shown: while the deposit of the last pay is not linked in the accounts,
 * that pay is still to come, and so is every charge due since it. Today
 * otherwise.
 */
export function planStart(
  pay: PayConfig,
  schedules: readonly ScheduleEntity[],
  today: string,
  paidIds: ReadonlySet<string> = new Set(),
): string {
  const payday = fortnightStart(pay, today);
  const unseen = expandSchedules(schedules, payday, today, paidIds).some(
    occurrence => occurrence.amount > 0,
  );
  return unseen ? payday : today;
}

/**
 * The investments to pay before the debts, as the dated charges they then
 * are. One without a date, or due outside the window, is left out.
 */
export function investmentCharges(
  projects: Project[],
  from: string,
  to: string,
): Occurrence[] {
  return projects.flatMap(project =>
    project.mandatory &&
    project.wished !== undefined &&
    project.wished >= from &&
    project.wished <= to
      ? [
          {
            id: `investissement/${project.id}`,
            name: project.name,
            date: project.wished,
            amount: -project.amount,
            recurring: false,
          },
        ]
      : [],
  );
}

/**
 * Gives each debt the one-off payments of the schedules tied to it. Recurring
 * ones are left out: they are its monthly payment already.
 */
export function withScheduledRepayments<T extends ConfiguredDebt>(
  debts: T[],
  occurrences: Occurrence[],
): T[] {
  return debts.map(debt => {
    const ids = debt.repaymentScheduleIds;
    if (!ids) {
      return debt;
    }
    return {
      ...debt,
      scheduledRepayments: occurrences
        .filter(
          o =>
            o.amount < 0 &&
            o.recurring === false &&
            o.scheduleId !== undefined &&
            ids.includes(o.scheduleId),
        )
        .map(o => ({ month: o.date.slice(0, 7), amount: -o.amount })),
    };
  });
}
